import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { AccentPalette } from '~/client/components/base/core/color';
import { useOrganizationBranding } from '~/client/hooks/use-organization-branding';

export type { AccentPalette } from '~/client/components/base/core/color';

const ACCENT_STORAGE_KEY = 'accent-palette';
const DEFAULT_ACCENT: AccentPalette = 'inked';

const VALID_PALETTES: AccentPalette[] = [
  'current',
  'neptune',
  'inked',
  'vichy',
  'sapphire',
  'opaline',
  'clockwork',
];

function isValidAccentPalette(value: unknown): value is AccentPalette {
  return (
    typeof value === 'string' && VALID_PALETTES.includes(value as AccentPalette)
  );
}

function readUserAccentOverride(): AccentPalette | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(ACCENT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isValidAccentPalette(parsed)) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return null;
}

interface AccentContextType {
  /** Resolved accent palette (user override > org default > app default) */
  accentPalette: AccentPalette;
  /** Organization's default accent palette (if set) */
  orgAccentPalette: AccentPalette | null;
  /** User's personal override (if set) */
  userAccentOverride: AccentPalette | null;
  /** Set user's personal override. Pass null to use org default. */
  setUserAccentOverride: (accent: AccentPalette | null) => void;
}

const AccentContext = createContext<AccentContextType>({
  accentPalette: DEFAULT_ACCENT,
  orgAccentPalette: null,
  userAccentOverride: null,
  setUserAccentOverride: () => {},
});

interface AccentProviderProps {
  children: React.ReactNode;
}

export const AccentProvider: React.FC<AccentProviderProps> = ({ children }) => {
  const { branding } = useOrganizationBranding();
  const [userAccentOverride, setUserAccentOverrideState] =
    useState<AccentPalette | null>(() => readUserAccentOverride());

  const orgAccentPalette = useMemo<AccentPalette | null>(() => {
    const orgAccent = (branding as { accentPalette?: string } | null)
      ?.accentPalette;
    if (isValidAccentPalette(orgAccent)) {
      return orgAccent;
    }
    return null;
  }, [branding]);

  const accentPalette = useMemo<AccentPalette>(() => {
    return userAccentOverride ?? orgAccentPalette ?? DEFAULT_ACCENT;
  }, [userAccentOverride, orgAccentPalette]);

  const setUserAccentOverride = useCallback((accent: AccentPalette | null) => {
    setUserAccentOverrideState(accent);
    try {
      if (accent) {
        window.localStorage.setItem(ACCENT_STORAGE_KEY, JSON.stringify(accent));
      } else {
        window.localStorage.removeItem(ACCENT_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const override = readUserAccentOverride();
    if (override !== userAccentOverride) {
      setUserAccentOverrideState(override);
    }
  }, []);

  const value: AccentContextType = {
    accentPalette,
    orgAccentPalette,
    userAccentOverride,
    setUserAccentOverride,
  };

  return (
    <AccentContext.Provider value={value}>{children}</AccentContext.Provider>
  );
};

export function useAccentPalette(): AccentContextType {
  const context = useContext(AccentContext);
  if (!context) {
    throw new Error('useAccentPalette must be used within AccentProvider');
  }
  return context;
}

export default AccentContext;
